import { FC, useCallback, useMemo } from 'react';
import { RadixTable, TColumn } from '@features/ui';
import {
  BillingActionType,
  BillingStatus,
  InvoiceSubscriptionRead,
} from '@src/types/billing.ts';
import { capitalize, cn, formatCurrency, formatDate } from '@lib/utils.ts';
import { DropdownAction } from '@features/user/billing/dropdownAction';

interface AccountSubscriptionsProps {
  onCancel?: (id: number) => void;
  onRenew?: (id: number) => void;
  data: InvoiceSubscriptionRead[];
}

export const AccountSubscriptions: FC<AccountSubscriptionsProps> = ({
  onCancel,
  onRenew,
  data,
}) => {
  const handleClick = useCallback(
    (type: BillingActionType, id?: number) => {
      if (!id) return;
      if (type === BillingActionType.Canceled) {
        onCancel?.(id);

        return;
      }
      onRenew?.(id);
    },
    [onCancel, onRenew]
  );

  const columns = useMemo(
    (): TColumn<InvoiceSubscriptionRead>[] => [
      {
        title: 'Started',
        render: (item) => (
          <p className={'font-medium'}>{formatDate(item?.begin_date, true)}</p>
        ),
      },
      {
        title: 'Account',
        render: (item) => {
          const accesses = item?.accesses?.filter(Boolean) ?? [];

          return (
            <div className={`flex flex-col gap-y-1`}>
              <div className={'flex flex-wrap gap-1'}>
                {accesses.length ? (
                  accesses.map((acc, index) => (
                    <div
                      key={String(
                        acc?.access_id ??
                          acc?.invoice_item_id ??
                          `${item?.invoice_id ?? 'subscription'}-${index}`
                      )}
                      className={
                        'min-w-[133px] w-fit min-h-[25px] px-2 py-1 items-center justify-center flex text-[#00E579] bg-[#14CA744D] rounded-[5px] text-[10px] font-medium break-words'
                      }
                    >
                      {acc?.product?.display_name_web ??
                        acc?.product?.title ??
                        '-'}
                    </div>
                  ))
                ) : (
                  <div
                    className={
                      'min-w-[133px] w-fit min-h-[25px] px-2 py-1 items-center justify-center flex text-[#00E579] bg-[#14CA744D] rounded-[5px] text-[10px] font-medium'
                    }
                  >
                    -
                  </div>
                )}
              </div>
              <p className={'font-medium'}>
                {item?.invoice_id != null ? `#${item.invoice_id}` : '-'}
              </p>
            </div>
          );
        },
      },
      {
        title: 'Renews/Expires',
        render: (item) => (
          <p className={'font-medium'}>
            {formatDate(item?.expire_date ?? undefined, true)}
          </p>
        ),
      },
      {
        title: 'Price',
        render: (item) => {
          const totalAmount = Number(item?.total_amount ?? 0);
          const formattedTotalAmount = formatCurrency(
            Number.isFinite(totalAmount) ? totalAmount : 0,
            2
          );

          return <p className={'font-medium'}>{`$${formattedTotalAmount}`}</p>;
        },
      },
      {
        title: 'Method',
        render: (item) => (
          <p className={'font-medium'}>
            {capitalize(item?.payment_system ?? '-')}
          </p>
        ),
      },
      {
        title: 'Status',
        render: (item) => {
          const bgColor = () => {
            switch (item?.subscription_status) {
              case BillingStatus.Active:
                return 'bg-[#198755]';
              case BillingStatus.Canceled:
                return 'bg-[#C72E2E]';
              case BillingStatus.Expired:
                return 'bg-lime';
              default:
                return '';
            }
          };

          const label = () => {
            switch (item?.subscription_status) {
              case BillingStatus.Active:
                return 'Active';
              case BillingStatus.Canceled:
                return 'Cancelled';
              case BillingStatus.Expired:
                return 'Expired';
              default:
                return '';
            }
          };

          return (
            <div
              className={cn(
                'flex items-center justify-center h-[26px] px-2 w-fit rounded-[5px]',
                bgColor()
              )}
            >
              <p>{label()}</p>
            </div>
          );
        },
      },
      {
        title: 'Actions',
        render: (item) => (
          <DropdownAction
            status={item?.subscription_status}
            onSelect={(value) => handleClick(value, item?.invoice_id)}
          />
        ),
      },
    ],
    [handleClick]
  );

  return (
    <div>
      <RadixTable columns={columns} data={data} />
    </div>
  );
};
